import {
  type DropTargetDropEvent,
  type DropTargetOptions,
  notify,
  toast,
  useDropTarget,
} from '@affine/component';
import { usePageHelper } from '@affine/core/blocksuite/block-suite-page-list/utils';
import { useBootSplashReady } from '@affine/core/bootstrap/boot-splash';
import {
  recordBootEvent,
  setBootBlocker,
} from '@affine/core/bootstrap/boot-trace';
import { buildSidebarCreateActions } from '@affine/core/modules/app-sidebar/views/area-actions/create-actions';
import { AuthService } from '@affine/core/modules/cloud';
import { AccessibleDocCacheStore } from '@affine/core/modules/doc/stores/accessible-doc-cache';
import {
  createDefaultDocCreateConfig,
  type DocCreateConfig,
} from '@affine/core/modules/doc-share';
import {
  FolderApiService,
  folderCreateErrorI18nKey,
  folderMoveErrorI18nKey,
  FolderMutationOutboxService,
  type FolderPermissions,
  getRememberedFolderPermissions,
  rememberFolderPermissions,
} from '@affine/core/modules/folder-api';
import {
  createDefaultFolderCreateConfig,
  type FolderCreateConfig,
} from '@affine/core/modules/folder-share';
import {
  NavigationPanelRevealService,
  NavigationPanelService,
} from '@affine/core/modules/navigation-panel';
import {
  AccessibleFolderCacheStore,
  type AccessibleFolderMeta,
  type FolderNode,
  FolderStore,
  OrganizeService,
  WorkspacePresenceStore,
} from '@affine/core/modules/organize';
import {
  ObjectPermissionMutationService,
  WorkspacePermissionService,
} from '@affine/core/modules/permissions';
import { useFeatureGate } from '@affine/core/modules/streamlain/hooks/use-feature-gate';
import { WorkspaceService } from '@affine/core/modules/workspace';
import type { AffineDNDData } from '@affine/core/types/dnd';
import { isBackendUnavailableError } from '@affine/core/utils/backend-availability';
import { type DocRole, Permission } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import {
  LiveData,
  useLiveData,
  useService,
  useServices,
} from '@toeverything/infra';
import { nanoid } from 'nanoid';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { CollapsibleSection } from '../../layouts/collapsible-section';
import { NavigationPanelDocNode } from '../../nodes/doc';
import { NavigationPanelFolderNode } from '../../nodes/folder';
import {
  DropEffect,
  forEachNavigationPanelDragSelectionItem,
  getNavigationPanelDragSelection,
  NavigationPanelTreeRoot,
  NavigationPanelTreeSelectionContext,
} from '../../tree';
import { rootDropZone } from '../../tree/root-drop-zone.css';
import { SidebarAreaHeaderActions } from '../../tree/sidebar-area-header-actions';
import { NavigationPanelSidebarSortModeProvider } from '../../tree/sidebar-sort-context';
import {
  compareSidebarManualOrderItems,
  sidebarManualOrderKey,
  type SidebarManualOrderPosition,
  useSidebarManualOrder,
} from '../../tree/use-sidebar-manual-order';
import {
  organizeChildrenDropEffect,
  organizeRootCanDrop,
  organizeRootDropEffect,
} from './dnd';
import { RootEmpty } from './empty';
import { isOrganizeEmptyStateReady } from './readiness';

function isPrivateNavigationSource(data: {
  source: { data: AffineDNDData['draggable'] };
}) {
  return (
    data.source.data.from?.at === 'navigation-panel:private:folder-node' ||
    data.source.data.from?.at === 'navigation-panel:private:root-doc'
  );
}

function getPrivateFolderDragName(data: DropTargetDropEvent<AffineDNDData>) {
  const from = data.source.data.from;
  return from?.at === 'navigation-panel:private:folder-node'
    ? from.folderName
    : undefined;
}

function getCachedDocSourceFolderId(
  accessibleFolderCache: AccessibleFolderCacheStore,
  docId: string
): string | null {
  const cachedFolder = accessibleFolderCache
    .getCachedFolders()
    .find(folder => folder.docIds.includes(docId));
  if (cachedFolder) {
    return cachedFolder.id;
  }

  const rootFolderId = accessibleFolderCache.getCachedWorkspaceRootFolderId();
  if (
    rootFolderId &&
    accessibleFolderCache.getCachedRootFolderDocIds().includes(docId)
  ) {
    return rootFolderId;
  }

  return null;
}

function createOptimisticFolderMeta({
  id,
  name,
  parentId,
  ownerUserId = null,
  visibility = 'public',
  inheritPermissions = true,
}: {
  id: string;
  name: string;
  parentId: string | null;
  ownerUserId?: string | null;
  visibility?: 'public' | 'private';
  inheritPermissions?: boolean;
}): AccessibleFolderMeta {
  return {
    id,
    name,
    parentId,
    ownerUserId,
    systemKind: null,
    visibility,
    inheritPermissions,
    isWorkspaceRoot: false,
    immutable: false,
    storageLimitBytes: null,
    storageUsedBytes: null,
    metadataOnly: false,
    docIds: [],
  };
}

const EMPTY_ROOT_FOLDER_DOC_IDS: readonly string[] = [];
const APP_AREA_ROOT_SYSTEM_KIND = 'app_area_root';
const PRIVATE_AREA_ROOTS_SYSTEM_KIND = 'private_area_roots';
const TEAMSPACE_ROOT_SYSTEM_KIND = 'teamspace_root';

type OrganizeRootItem =
  | { key: string; label: string; type: 'folder'; node: FolderNode }
  | { key: string; label: string; type: 'doc'; docId: string };

function customAppAreaRootFolderId(workspaceId: string) {
  return `custom_app_area_${workspaceId}`;
}

function privateAreaRootsFolderId(workspaceId: string) {
  return `private_area_roots_${workspaceId}`;
}

export const NavigationPanelOrganize = () => {
  const {
    organizeService,
    navigationPanelService,
    navigationPanelRevealService,
    folderMutationOutboxService: folderMutationOutbox,
    objectPermissionMutationService: objectPermissionMutation,
    workspaceService,
  } = useServices({
    OrganizeService,
    NavigationPanelService,
    NavigationPanelRevealService,
    FolderMutationOutboxService,
    ObjectPermissionMutationService,
    WorkspaceService,
  });
  const accessibleFolderCache = useService(AccessibleFolderCacheStore);
  const accessibleDocCache = useService(AccessibleDocCacheStore);
  const folderApiService = useService(FolderApiService);
  const folderStore = useService(FolderStore);
  const permissionService = useService(WorkspacePermissionService);
  const authService = useService(AuthService);
  const presenceStore = useService(WorkspacePresenceStore);
  const selectionContext = useContext(NavigationPanelTreeSelectionContext);
  const account = useLiveData(authService.session.account$);
  const path = useMemo(() => ['organize'], []);
  const collapsed = useLiveData(navigationPanelService.collapsed$(path));
  const [newFolderId, setNewFolderId] = useState<string | null>(null);
  const t = useI18n();

  const folderTree = organizeService.folderTree;
  const rootFolder = folderTree.rootFolder;
  const workspaceId = workspaceService.workspace.id;
  const { createPage } = usePageHelper(
    workspaceService.workspace.docCollection
  );
  const isCloudWorkspace = workspaceService.workspace.flavour !== 'local';
  const workspaceRole = useLiveData(permissionService.permission.role$);
  const effectiveWorkspaceRole =
    workspaceRole ?? workspaceService.workspace.meta.role ?? null;
  const canCreateRootFolderFromRole =
    !isCloudWorkspace ||
    effectiveWorkspaceRole === Permission.Owner ||
    effectiveWorkspaceRole === Permission.Admin ||
    effectiveWorkspaceRole === Permission.Editor;
  const canWriteRootYjsFolders =
    !isCloudWorkspace ||
    effectiveWorkspaceRole === Permission.Owner ||
    effectiveWorkspaceRole === Permission.Admin;

  // sl-jmyl.6: Start presence listener (GraphQL initial + Direct Socket push)
  useEffect(() => {
    if (!isCloudWorkspace) return;
    presenceStore.startListening(workspaceId);
  }, [isCloudWorkspace, workspaceId, presenceStore]);

  const folders = useLiveData(rootFolder.sortedChildren$);
  const accessibleFolders = useLiveData(
    useMemo(
      () =>
        LiveData.from(
          accessibleFolderCache.watchAllAccessibleFolders(),
          accessibleFolderCache.getCachedFolders()
        ),
      [accessibleFolderCache]
    )
  );
  const accessibleFolderIdSet = useMemo(
    () => new Set(accessibleFolders.map(folder => folder.id)),
    [accessibleFolders]
  );
  const folderSystemKindById = useMemo(
    () =>
      new Map(accessibleFolders.map(folder => [folder.id, folder.systemKind])),
    [accessibleFolders]
  );
  const appAreaRootFolderId = useMemo(
    () => customAppAreaRootFolderId(workspaceId),
    [workspaceId]
  );
  const privateAreaRootsFolderIdValue = useMemo(
    () => privateAreaRootsFolderId(workspaceId),
    [workspaceId]
  );
  const isLoading = useLiveData(folderTree.isLoading$);
  const folderCachePopulated = useLiveData(accessibleFolderCache.isPopulated$);
  const folderLocalCacheLoaded = useLiveData(
    accessibleFolderCache.isLocalCacheLoaded$
  );
  const folderServerConfirmed = useLiveData(
    accessibleFolderCache.isServerConfirmed$
  );
  const docCachePopulated = useLiveData(accessibleDocCache.isPopulated$);
  const docLocalCacheLoaded = useLiveData(
    accessibleDocCache.isLocalCacheLoaded$
  );
  const docServerConfirmed = useLiveData(accessibleDocCache.isServerConfirmed$);
  const docCacheReady = docLocalCacheLoaded || docCachePopulated;
  const folderCacheReady = folderLocalCacheLoaded || folderCachePopulated;
  const { isLocalEntitlementReady } = useFeatureGate();
  const organizeBootReady = isCloudWorkspace
    ? folderCacheReady && docCacheReady && isLocalEntitlementReady
    : !isLoading;
  useBootSplashReady(organizeBootReady);
  useEffect(() => {
    recordBootEvent('organize-readiness-state', {
      docCachePopulated,
      docLocalCacheLoaded,
      folderCachePopulated,
      folderLocalCacheLoaded,
      isLoading,
      isLocalEntitlementReady,
      ready: organizeBootReady,
      workspaceType: isCloudWorkspace ? 'cloud' : 'local',
    });
    if (!isCloudWorkspace || organizeBootReady) {
      return;
    }
    if (!folderCacheReady) {
      setBootBlocker('folder-cache-local');
    } else if (!docCacheReady) {
      setBootBlocker('doc-cache-local');
    } else if (!isLocalEntitlementReady) {
      setBootBlocker('feature-entitlement-local');
    }
  }, [
    docCacheReady,
    docCachePopulated,
    docLocalCacheLoaded,
    folderCacheReady,
    folderCachePopulated,
    folderLocalCacheLoaded,
    isCloudWorkspace,
    isLoading,
    isLocalEntitlementReady,
    organizeBootReady,
  ]);

  useEffect(() => {
    if (!isCloudWorkspace) return;
    accessibleFolderCache.prime();
    accessibleDocCache.prime();
  }, [accessibleDocCache, accessibleFolderCache, isCloudWorkspace]);
  const organizeEmptyStateReady = isOrganizeEmptyStateReady({
    docServerConfirmed,
    folderServerConfirmed,
    isCloudWorkspace,
    isLoading,
  });

  // ─── Workspace Root Folder Flatten ─────────────────────────────────
  // The backend auto-links docs to the workspace root folder. To avoid
  // showing an invisible "root" folder in Organize, we flatten its
  // doc-children to root level and hide the folder itself.
  const workspaceRootFolderId$ = useMemo(
    () =>
      isCloudWorkspace
        ? LiveData.from(
            accessibleFolderCache.watchWorkspaceRootFolderId$(),
            null
          )
        : null,
    [isCloudWorkspace, accessibleFolderCache]
  );
  const workspaceRootFolderId = useLiveData(workspaceRootFolderId$);
  const [rootFolderPermissionState, setRootFolderPermissionState] = useState<{
    folderId: string;
    permissions: FolderPermissions | null;
  } | null>(null);
  const rootFolderPermissions =
    rootFolderPermissionState?.folderId === workspaceRootFolderId
      ? rootFolderPermissionState.permissions
      : null;

  useEffect(() => {
    if (!isCloudWorkspace || !workspaceRootFolderId) {
      setRootFolderPermissionState(null);
      return;
    }

    let cancelled = false;
    folderApiService
      .getFolder(workspaceId, workspaceRootFolderId)
      .then(folder => {
        if (cancelled) return;
        rememberFolderPermissions(
          workspaceId,
          workspaceRootFolderId,
          folder?.permissions ?? null
        );
        setRootFolderPermissionState({
          folderId: workspaceRootFolderId,
          permissions: folder?.permissions ?? null,
        });
      })
      .catch(error => {
        if (cancelled) return;
        if (isBackendUnavailableError(error)) {
          const rememberedPermissions = getRememberedFolderPermissions(
            workspaceId,
            workspaceRootFolderId
          );
          if (rememberedPermissions) {
            setRootFolderPermissionState({
              folderId: workspaceRootFolderId,
              permissions: rememberedPermissions,
            });
            return;
          }
        }
        rememberFolderPermissions(workspaceId, workspaceRootFolderId, null);
        setRootFolderPermissionState({
          folderId: workspaceRootFolderId,
          permissions: null,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [folderApiService, isCloudWorkspace, workspaceId, workspaceRootFolderId]);

  const canCreateRootFolder =
    canCreateRootFolderFromRole ||
    rootFolderPermissions?.Folder_CreateSubfolder === true;
  const canCreateRootDocument =
    rootFolderPermissions?.Folder_AddDoc ?? canCreateRootFolderFromRole;

  // Root folder docs come from PostgreSQL folder links, not Y.js.
  // Root placement is explicit through the folder mutation outbox.
  const rootFolderDocIds$ = useMemo(
    () =>
      isCloudWorkspace
        ? LiveData.from(
            accessibleFolderCache.watchRootFolderDocIds$(),
            accessibleFolderCache.getCachedRootFolderDocIds()
          )
        : null,
    [isCloudWorkspace, accessibleFolderCache]
  );
  const rootFolderDocIds =
    useLiveData(rootFolderDocIds$) ?? EMPTY_ROOT_FOLDER_DOC_IDS;

  useEffect(() => {
    if (!isCloudWorkspace || rootFolderDocIds.length === 0) {
      return;
    }
    accessibleDocCache
      .fetchFolderDocs(
        [...rootFolderDocIds],
        undefined,
        'sidebar-root-folder-doc-ids'
      )
      .catch(() => {});
  }, [accessibleDocCache, isCloudWorkspace, rootFolderDocIds]);
  const accessibleDocs$ = useMemo(
    () =>
      LiveData.from(
        accessibleDocCache.watchAllAccessibleDocs(),
        accessibleDocCache.getCachedDocs()
      ),
    [accessibleDocCache]
  );
  const accessibleDocs = useLiveData(accessibleDocs$);
  const docTitleById = useMemo(
    () => new Map(accessibleDocs.map(doc => [doc.id, doc.title])),
    [accessibleDocs]
  );
  const docTrashStateById = useMemo(
    () => new Map(accessibleDocs.map(doc => [doc.id, doc.trashed])),
    [accessibleDocs]
  );

  // System roots render through their own top-level area sections. FolderStore
  // is the canonical Public-tree projection and excludes private folders at
  // every hierarchy depth.
  const visibleFolders = useMemo(
    () =>
      folders.filter(folder => {
        const folderId = folder.id as string;
        const systemKind = folderSystemKindById.get(folderId);
        return (
          (!isCloudWorkspace || accessibleFolderIdSet.has(folderId)) &&
          folderId !== workspaceRootFolderId &&
          folderId !== appAreaRootFolderId &&
          folderId !== privateAreaRootsFolderIdValue &&
          systemKind !== APP_AREA_ROOT_SYSTEM_KIND &&
          systemKind !== PRIVATE_AREA_ROOTS_SYSTEM_KIND &&
          systemKind !== TEAMSPACE_ROOT_SYSTEM_KIND
        );
      }),
    [
      appAreaRootFolderId,
      accessibleFolderIdSet,
      folderSystemKindById,
      folders,
      isCloudWorkspace,
      privateAreaRootsFolderIdValue,
      workspaceRootFolderId,
    ]
  );
  const rootOrderStorageKey = useMemo(
    () => `streamlain:organize-root-manual-order:${workspaceId}`,
    [workspaceId]
  );
  const rootItems = useMemo<OrganizeRootItem[]>(
    () => [
      ...visibleFolders.map(node => ({
        key: sidebarManualOrderKey('folder', node.id as string),
        label: node.name$.value,
        type: 'folder' as const,
        node,
      })),
      ...rootFolderDocIds
        .filter(docId => docTrashStateById.get(docId) !== true)
        .map(docId => ({
          key: sidebarManualOrderKey('doc', docId),
          label: docTitleById.get(docId) ?? '',
          type: 'doc' as const,
          docId,
        })),
    ],
    [docTitleById, docTrashStateById, rootFolderDocIds, visibleFolders]
  );
  const {
    orderedItems: orderedRootItems,
    placeItem: placeRootItem,
    setSortMode: setRootSortMode,
    sortMode: rootSortMode,
  } = useSidebarManualOrder(
    rootOrderStorageKey,
    rootItems,
    compareSidebarManualOrderItems,
    { manualBase: 'source' }
  );

  const handleRootDocCreated = useCallback(
    (config: DocCreateConfig) => {
      if (!canCreateRootDocument) {
        toast('Workspace Reader kann keine Dokumente erstellen.');
        return;
      }
      const newDoc = createPage(undefined, {
        createInWorkspaceRoot: true,
        show: false,
        title: config.name,
      });
      accessibleFolderCache.optimisticAddToRoot(newDoc.id);
      if (rootSortMode === 'manual') {
        placeRootItem(sidebarManualOrderKey('doc', newDoc.id));
      }
      const permissionGrants = [
        ...config.pendingInvites.map(invite =>
          objectPermissionMutation.execute({
            action: 'grant',
            principal: { id: invite.userId, kind: 'user' },
            role: invite.role as DocRole,
            target: { id: newDoc.id, kind: 'doc' },
          })
        ),
        ...config.pendingGroupInvites.map(invite =>
          objectPermissionMutation.execute({
            action: 'grant',
            principal: { id: invite.groupId, kind: 'group' },
            role: invite.role as DocRole,
            target: { id: newDoc.id, kind: 'doc' },
          })
        ),
      ];
      void Promise.all(permissionGrants).catch(error => {
        console.error('Failed to apply document permissions:', error);
        notify.error({
          title:
            t[
              'com.streamlain.rootAppSidebar.organize.folder.error.doc-permissions-failed'
            ](),
        });
      });
      track.$.navigationPanel.organize.createOrganizeItem({
        type: 'link',
        target: 'doc',
      });
      navigationPanelService.setCollapsed(path, false);
      navigationPanelRevealService.revealDoc(newDoc.id, {
        openAfterRenameSubmit: true,
        rename: true,
        source: 'doc-create',
        workspaceId,
      });
    },
    [
      accessibleFolderCache,
      canCreateRootDocument,
      createPage,
      navigationPanelRevealService,
      navigationPanelService,
      objectPermissionMutation,
      path,
      placeRootItem,
      rootSortMode,
      t,
      workspaceId,
    ]
  );

  // Creates immediately; the rendered node enters inline rename mode.
  const handleFolderCreated = useCallback(
    async (config: FolderCreateConfig) => {
      try {
        const folderId = canWriteRootYjsFolders
          ? rootFolder.createFolder(config.name, rootFolder.indexAt('before'))
          : nanoid();
        setNewFolderId(folderId);
        accessibleFolderCache.optimisticUpsertFolder(
          createOptimisticFolderMeta({
            id: folderId,
            name: config.name,
            parentId: null,
            ownerUserId: account?.id ?? null,
            visibility: config.visibility,
            inheritPermissions: config.inheritPermissions,
          }),
          { preserveUntilServerAck: true }
        );
        if (rootSortMode === 'manual') {
          placeRootItem(sidebarManualOrderKey('folder', folderId));
        }
        track.$.navigationPanel.organize.createOrganizeItem({ type: 'folder' });
        navigationPanelService.setCollapsed(path, false);

        await folderMutationOutbox.enqueueCreateFolder({
          folderId,
          name: config.name,
          parentId: null,
          visibility: config.visibility,
          inheritPermissions: config.inheritPermissions,
        });

        await objectPermissionMutation.grantPendingAssignments(
          { id: folderId, kind: 'folder' },
          config
        );
      } catch (err) {
        console.error('Failed to create backend folder:', err);
        notify.error({
          title: t[folderCreateErrorI18nKey(err)](),
        });
      }
    },
    [
      rootFolder,
      canWriteRootYjsFolders,
      navigationPanelService,
      path,
      folderMutationOutbox,
      objectPermissionMutation,
      accessibleFolderCache,
      account?.id,
      placeRootItem,
      rootSortMode,
      t,
    ]
  );

  const handleCreateDoc = useCallback(() => {
    handleRootDocCreated(createDefaultDocCreateConfig(t['Untitled']()));
  }, [handleRootDocCreated, t]);

  const handleCreateFolder = useCallback(() => {
    void handleFolderCreated(
      createDefaultFolderCreateConfig(
        'public-folder',
        t['com.streamlain.rootAppSidebar.organize.create.folder']()
      )
    );
  }, [handleFolderCreated, t]);

  const movePrivateFolderToWorkspaceRoot = useCallback(
    (
      data: DropTargetDropEvent<AffineDNDData>,
      targetIndex: string,
      rootPosition?: SidebarManualOrderPosition
    ) => {
      const entity = data.source.data.entity;
      if (entity?.type !== 'folder') {
        return;
      }

      const optimisticSnapshot =
        accessibleFolderCache.captureOptimisticSnapshot();
      const existingInfo = folderStore.getYjsFolderNodeInfo(entity.id);
      const folderName =
        accessibleFolderCache.getCachedFolder(entity.id)?.name ??
        existingInfo?.data ??
        getPrivateFolderDragName(data) ??
        'Folder';
      try {
        if (existingInfo) {
          rootFolder.moveHere(entity.id, targetIndex);
        } else {
          rootFolder.createFolder(folderName, targetIndex, entity.id);
        }
        accessibleFolderCache.optimisticMoveFolder(
          entity.id,
          null,
          {
            visibility: 'public',
            systemKind: null,
            name: folderName,
          },
          undefined,
          {
            name: folderName,
            ownerUserId: account?.id ?? null,
            visibility: 'public',
            systemKind: null,
          }
        );
        if (rootPosition) {
          placeRootItem(
            sidebarManualOrderKey('folder', entity.id),
            rootPosition
          );
        }
        navigationPanelService.setCollapsed(path, false);
      } catch (error) {
        console.error('Failed to move private folder to organize:', error);
        accessibleFolderCache.restoreOptimisticSnapshot(optimisticSnapshot);
        toast(t[folderMoveErrorI18nKey(error)]());
        return;
      }

      void folderMutationOutbox
        .enqueueMoveFolder(entity.id, null)
        .catch((error: unknown) => {
          console.error('Failed to move private folder to organize:', error);
          if (existingInfo) {
            try {
              const previousParent = existingInfo.parentId
                ? folderTree.folderNode$(existingInfo.parentId).value
                : folderTree.rootFolder;
              previousParent?.moveHere(entity.id, existingInfo.index);
            } catch {
              accessibleFolderCache.fetchAccessibleFolders().catch(() => {});
            }
          } else {
            try {
              folderTree.folderNode$(entity.id).value?.delete();
            } catch {
              // Cache rollback below restores the private sidebar.
            }
          }
          accessibleFolderCache.restoreOptimisticSnapshot(optimisticSnapshot);
          toast(t[folderMoveErrorI18nKey(error)]());
        });
      track.$.navigationPanel.organize.moveOrganizeItem({ type: 'folder' });
    },
    [
      accessibleFolderCache,
      account?.id,
      folderMutationOutbox,
      folderStore,
      folderTree,
      navigationPanelService,
      path,
      placeRootItem,
      rootFolder,
      t,
    ]
  );

  const canMoveDocToWorkspaceRoot = useCallback(
    (docId: string) => {
      if (rootFolderPermissions?.Folder_AddDoc === false) {
        return false;
      }

      const sourceFolderId = getCachedDocSourceFolderId(
        accessibleFolderCache,
        docId
      );
      if (!sourceFolderId || sourceFolderId === workspaceRootFolderId) {
        return true;
      }

      const sourceFolder =
        accessibleFolderCache.getCachedFolder(sourceFolderId);
      if (sourceFolder?.systemKind === 'private_mail') {
        return false;
      }

      const sourcePermissions = getRememberedFolderPermissions(
        workspaceId,
        sourceFolderId
      );
      return sourcePermissions?.Folder_RemoveDoc !== false;
    },
    [
      accessibleFolderCache,
      rootFolderPermissions,
      workspaceId,
      workspaceRootFolderId,
    ]
  );

  const moveDocToWorkspaceRoot = useCallback(
    (
      data: DropTargetDropEvent<AffineDNDData>,
      rootPosition?: SidebarManualOrderPosition
    ) => {
      const entity = data.source.data.entity;
      if (entity?.type !== 'doc') {
        return;
      }
      if (!canMoveDocToWorkspaceRoot(entity.id)) {
        notify.error({
          title:
            t[
              'com.streamlain.rootAppSidebar.organize.folder.error.move-doc-failed'
            ](),
        });
        return;
      }

      const sourceFolderId = getCachedDocSourceFolderId(
        accessibleFolderCache,
        entity.id
      );
      const alreadyInWorkspaceRoot =
        workspaceRootFolderId !== null &&
        sourceFolderId === workspaceRootFolderId;
      const optimisticSnapshot =
        accessibleFolderCache.captureOptimisticSnapshot();

      accessibleFolderCache.optimisticAddToRoot(entity.id);
      if (rootPosition) {
        placeRootItem(sidebarManualOrderKey('doc', entity.id), rootPosition);
      }
      navigationPanelService.setCollapsed(path, false);

      if (alreadyInWorkspaceRoot) {
        return;
      }

      void folderMutationOutbox
        .enqueueRemoveDocFromFolder(entity.id)
        .catch((error: unknown) => {
          console.error('Failed to move doc to organize:', error);
          accessibleFolderCache.restoreOptimisticSnapshot(optimisticSnapshot);
          notify.error({
            title:
              t[
                folderMoveErrorI18nKey(
                  error,
                  'com.streamlain.rootAppSidebar.organize.folder.error.move-doc-failed'
                )
              ](),
          });
        });
      track.$.navigationPanel.organize.moveOrganizeItem({ type: 'doc' });
    },
    [
      accessibleFolderCache,
      canMoveDocToWorkspaceRoot,
      folderMutationOutbox,
      navigationPanelService,
      path,
      placeRootItem,
      t,
      workspaceRootFolderId,
    ]
  );

  const handleSidebarLinkDropDisabled = useCallback(
    (_data: DropTargetDropEvent<AffineDNDData>) => {
      return;
    },
    []
  );

  const moveSharedFolderToWorkspaceRoot = useCallback(
    (
      folderId: string,
      targetIndex: string,
      rootPosition?: SidebarManualOrderPosition
    ) => {
      const optimisticSnapshot =
        accessibleFolderCache.captureOptimisticSnapshot();
      const previousInfo = folderStore.getYjsFolderNodeInfo(folderId);
      const folderName =
        accessibleFolderCache.getCachedFolder(folderId)?.name ??
        previousInfo?.data ??
        'Folder';
      try {
        if (previousInfo) {
          rootFolder.moveHere(folderId, targetIndex);
        } else {
          rootFolder.createFolder(folderName, targetIndex, folderId);
        }
        accessibleFolderCache.optimisticMoveFolder(
          folderId,
          null,
          {
            name: folderName,
          },
          undefined,
          {
            name: folderName,
            ownerUserId: account?.id ?? null,
            visibility: 'public',
          }
        );
        if (rootPosition) {
          placeRootItem(
            sidebarManualOrderKey('folder', folderId),
            rootPosition
          );
        }
      } catch (error) {
        console.error('Failed to move folder to organize:', error);
        accessibleFolderCache.restoreOptimisticSnapshot(optimisticSnapshot);
        toast(t[folderMoveErrorI18nKey(error)]());
        return;
      }
      void folderMutationOutbox
        .enqueueMoveFolder(folderId, null)
        .catch((error: unknown) => {
          console.error('Failed to move folder to organize:', error);
          if (previousInfo) {
            try {
              const previousParent = previousInfo.parentId
                ? folderTree.folderNode$(previousInfo.parentId).value
                : folderTree.rootFolder;
              previousParent?.moveHere(folderId, previousInfo.index);
            } catch {
              accessibleFolderCache.fetchAccessibleFolders().catch(() => {});
            }
          } else {
            try {
              folderTree.folderNode$(folderId).value?.delete();
            } catch {
              // Cache rollback below restores the sidebar.
            }
          }
          accessibleFolderCache.restoreOptimisticSnapshot(optimisticSnapshot);
          toast(t[folderMoveErrorI18nKey(error)]());
        });
      track.$.navigationPanel.organize.moveOrganizeItem({ type: 'folder' });
    },
    [
      accessibleFolderCache,
      account?.id,
      folderMutationOutbox,
      folderStore,
      folderTree,
      placeRootItem,
      rootFolder,
      t,
    ]
  );

  const handleRootDrop = useCallback(
    (data: DropTargetDropEvent<AffineDNDData>) => {
      const entity = data.source.data.entity;
      if (!entity) {
        return;
      }

      if (getNavigationPanelDragSelection(data).length > 1) {
        forEachNavigationPanelDragSelectionItem(data, itemData => {
          const itemEntity = itemData.source.data.entity;
          if (!itemEntity) {
            return;
          }
          if (itemEntity.type === 'doc') {
            moveDocToWorkspaceRoot(itemData);
            return;
          }
          if (!canCreateRootFolder) {
            toast('Workspace Reader kann keine Ordner erstellen.');
            return;
          }
          if (isPrivateNavigationSource(itemData)) {
            if (itemEntity.type === 'folder') {
              movePrivateFolderToWorkspaceRoot(
                itemData,
                rootFolder.indexAt('before')
              );
            }
            return;
          }
          if (itemEntity.type === 'folder') {
            moveSharedFolderToWorkspaceRoot(
              itemEntity.id,
              rootFolder.indexAt('before')
            );
          }
        });
        selectionContext?.clearSelection();
        return;
      }

      if (entity.type === 'doc') {
        moveDocToWorkspaceRoot(data);
        return;
      }

      if (!canCreateRootFolder) {
        toast('Workspace Reader kann keine Ordner erstellen.');
        return;
      }

      if (isPrivateNavigationSource(data)) {
        if (entity.type === 'folder') {
          movePrivateFolderToWorkspaceRoot(data, rootFolder.indexAt('before'));
        }
        return;
      }

      if (entity.type === 'folder') {
        moveSharedFolderToWorkspaceRoot(
          entity.id,
          rootFolder.indexAt('before')
        );
      }
    },
    [
      canCreateRootFolder,
      moveDocToWorkspaceRoot,
      movePrivateFolderToWorkspaceRoot,
      moveSharedFolderToWorkspaceRoot,
      rootFolder,
      selectionContext,
    ]
  );
  const canDropOnOrganizeRoot = useMemo<
    DropTargetOptions<AffineDNDData>['canDrop']
  >(
    () => args => {
      const entity = args.source.data.entity;
      if (entity?.type === 'doc') {
        return canMoveDocToWorkspaceRoot(entity.id);
      }
      if (entity?.type === 'folder' && isPrivateNavigationSource(args)) {
        return canCreateRootFolder;
      }
      return canCreateRootFolder && organizeRootCanDrop(args);
    },
    [canCreateRootFolder, canMoveDocToWorkspaceRoot]
  );

  const {
    dropTargetRef: organizeRootDropTargetRef,
    draggedOverDraggable: organizeRootDraggedOverDraggable,
    draggedOverPosition: organizeRootDraggedOverPosition,
  } = useDropTarget<AffineDNDData>(
    () => ({
      data: { at: 'navigation-panel:organize:root' },
      onDrop: handleRootDrop,
      canDrop: canDropOnOrganizeRoot,
      allowExternal: true,
    }),
    [canDropOnOrganizeRoot, handleRootDrop]
  );
  const {
    dropTargetRef: organizeRootContentDropTargetRef,
    draggedOverDraggable: organizeRootContentDraggedOverDraggable,
    draggedOverPosition: organizeRootContentDraggedOverPosition,
  } = useDropTarget<AffineDNDData>(
    () => ({
      data: { at: 'navigation-panel:organize:root-content' },
      onDrop: handleRootDrop,
      canDrop: canDropOnOrganizeRoot,
      allowExternal: true,
    }),
    [canDropOnOrganizeRoot, handleRootDrop]
  );

  const handleRootSiblingDrop = useCallback(
    (
      data: DropTargetDropEvent<AffineDNDData>,
      targetIndex: string,
      rootPosition: SidebarManualOrderPosition
    ) => {
      if (getNavigationPanelDragSelection(data).length > 1) {
        forEachNavigationPanelDragSelectionItem(data, itemData => {
          handleRootSiblingDrop(itemData, targetIndex, rootPosition);
        });
        selectionContext?.clearSelection();
        return;
      }
      if (isPrivateNavigationSource(data)) {
        const entity = data.source.data.entity;
        if (!entity) {
          return;
        }
        if (entity.type === 'doc') {
          moveDocToWorkspaceRoot(data, rootPosition);
          return;
        }
        if (entity.type === 'folder') {
          movePrivateFolderToWorkspaceRoot(data, targetIndex, rootPosition);
        }
        return;
      }
      if (data.source.data.entity?.type === 'doc') {
        moveDocToWorkspaceRoot(data, rootPosition);
      } else if (data.source.data.entity?.type === 'folder') {
        moveSharedFolderToWorkspaceRoot(
          data.source.data.entity.id,
          targetIndex,
          rootPosition
        );
      } else if (
        data.source.data.from?.at === 'navigation-panel:organize:folder-node'
      ) {
        return;
      } else {
        toast(t['com.streamlain.rootAppSidebar.organize.root-folder-only']());
      }
    },
    [
      moveDocToWorkspaceRoot,
      movePrivateFolderToWorkspaceRoot,
      moveSharedFolderToWorkspaceRoot,
      selectionContext,
      t,
    ]
  );

  const handleOnChildrenDrop = useCallback(
    (data: DropTargetDropEvent<AffineDNDData>, node?: FolderNode) => {
      if (!node || !node.id) {
        return; // never happens
      }
      if (
        data.treeInstruction?.type === 'reorder-above' ||
        data.treeInstruction?.type === 'reorder-below'
      ) {
        const at =
          data.treeInstruction?.type === 'reorder-below' ? 'after' : 'before';
        handleRootSiblingDrop(data, rootFolder.indexAt(at, node.id), {
          at,
          key: sidebarManualOrderKey('folder', node.id),
        });
      } else {
        return; // not supported
      }
    },
    [handleRootSiblingDrop, rootFolder]
  );

  const rootDocDropIndex = useCallback(
    (at: 'before' | 'after', docId: string) => {
      try {
        return rootFolder.indexAt(at, docId);
      } catch {
        return rootFolder.indexAt('after');
      }
    },
    [rootFolder]
  );

  const handleOnRootDocDrop = useCallback(
    (data: DropTargetDropEvent<AffineDNDData>, docId: string) => {
      if (
        data.treeInstruction?.type !== 'reorder-above' &&
        data.treeInstruction?.type !== 'reorder-below'
      ) {
        return;
      }
      const at =
        data.treeInstruction?.type === 'reorder-below' ? 'after' : 'before';
      handleRootSiblingDrop(data, rootDocDropIndex(at, docId), {
        at,
        key: sidebarManualOrderKey('doc', docId),
      });
    },
    [handleRootSiblingDrop, rootDocDropIndex]
  );

  const handleChildrenCanDrop = useMemo<
    DropTargetOptions<AffineDNDData>['canDrop']
  >(
    () => args => {
      const entityType = args.source.data.entity?.type;
      // Allow folders to be reordered at root
      if (!canCreateRootFolder) return false;
      if (
        rootSortMode !== 'manual' &&
        (args.treeInstruction?.type === 'reorder-above' ||
          args.treeInstruction?.type === 'reorder-below')
      ) {
        return false;
      }
      if (entityType === 'doc') {
        const docId = args.source.data.entity?.id;
        return typeof docId === 'string' && canMoveDocToWorkspaceRoot(docId);
      }
      if (entityType === 'folder') return true;
      return false;
    },
    [canCreateRootFolder, canMoveDocToWorkspaceRoot, rootSortMode]
  );

  useEffect(() => {
    if (collapsed) setNewFolderId(null); // reset new folder id to clear the renaming state
  }, [collapsed]);

  return (
    <>
      <CollapsibleSection
        path={path}
        testId="sidebar-public"
        title={
          t['com.streamlain.rootAppSidebar.publicArea']?.() ??
          t['com.streamlain.rootAppSidebar.organize']()
        }
        headerRef={organizeRootDropTargetRef}
        headerTestId="navigation-panel-organize-category-divider"
        actions={
          <>
            <SidebarAreaHeaderActions
              create={{
                actions: buildSidebarCreateActions({
                  document: canCreateRootDocument
                    ? {
                        label:
                          t[
                            'com.streamlain.rootAppSidebar.organize.create.document'
                          ](),
                        onSelect: handleCreateDoc,
                      }
                    : undefined,
                  folder: canCreateRootFolder
                    ? {
                        label:
                          t[
                            'com.streamlain.rootAppSidebar.organize.create.folder'
                          ](),
                        onSelect: handleCreateFolder,
                      }
                    : undefined,
                }),
                tooltip:
                  t['com.streamlain.rootAppSidebar.organize.create.tooltip'](),
              }}
              options={{
                sortMode: rootSortMode,
                onSortModeChange: setRootSortMode,
                testId: 'navigation-panel-organize-options-button',
              }}
            />
            {organizeRootDraggedOverDraggable && (
              <DropEffect
                position={organizeRootDraggedOverPosition}
                dropEffect={organizeRootDropEffect({
                  source: organizeRootDraggedOverDraggable,
                  treeInstruction: null,
                })}
              />
            )}
            {organizeRootContentDraggedOverDraggable && (
              <DropEffect
                position={organizeRootContentDraggedOverPosition}
                dropEffect={organizeRootDropEffect({
                  source: organizeRootContentDraggedOverDraggable,
                  treeInstruction: null,
                })}
              />
            )}
          </>
        }
      >
        <div
          ref={organizeRootContentDropTargetRef}
          className={rootDropZone}
          data-testid="navigation-panel-organize-root-drop-zone"
        >
          <NavigationPanelSidebarSortModeProvider mode={rootSortMode}>
            <NavigationPanelTreeRoot
              placeholder={
                !organizeEmptyStateReady ? (
                  <RootEmpty isLoading onDrop={handleSidebarLinkDropDisabled} />
                ) : (
                  <RootEmpty
                    onClickCreate={
                      canCreateRootFolder ? handleCreateFolder : undefined
                    }
                    onDrop={handleRootDrop}
                  />
                )
              }
            >
              {orderedRootItems.map(item =>
                item.type === 'doc' ? (
                  <NavigationPanelDocNode
                    key={item.key}
                    docId={item.docId}
                    onDrop={data => handleOnRootDocDrop(data, item.docId)}
                    dropEffect={organizeChildrenDropEffect}
                    canDrop={handleChildrenCanDrop}
                    hideLinkedChildren
                    location={{
                      at: 'navigation-panel:organize:root-doc',
                      nodeId: item.docId,
                    }}
                    parentPath={path}
                  />
                ) : (
                  <NavigationPanelFolderNode
                    key={item.key}
                    nodeId={item.node.id as string}
                    defaultRenaming={item.node.id === newFolderId}
                    onRenamingChange={renaming => {
                      if (!renaming && item.node.id === newFolderId) {
                        setNewFolderId(null);
                      }
                    }}
                    onDrop={handleOnChildrenDrop}
                    dropEffect={organizeChildrenDropEffect}
                    canDrop={handleChildrenCanDrop}
                    location={{
                      at: 'navigation-panel:organize:folder-node',
                      nodeId: item.node.id as string,
                    }}
                    parentPath={path}
                  />
                )
              )}
            </NavigationPanelTreeRoot>
          </NavigationPanelSidebarSortModeProvider>
        </div>
      </CollapsibleSection>
    </>
  );
};
